import React from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import * as Styled from "./roi-requests.styled";
import { SectionedDataTable } from "../../../layout/sectioned-data-table/ui/sectioned-data-table";
import {
  RoiRequestCreateFlow,
  RoiRequestCreateTriggerButton,
} from "../../../features/roi-request-create/ui/roi-request-create-flow";
import {
  IDemoPersistedRoiRequest,
  getDemoRoiRequestById,
  getDemoStoredRoiRequests,
  mapDemoRequestsToRows,
  mergeDemoRoiRequests,
  saveDemoRoiRequest,
} from "../../../features/roi-request-create/model/roi-request-create.storage";
import { downloadRoiRequestPdf } from "../../../features/roi-request-pdf/model/download-roi-request-pdf";
import { useRoiRequestDuplicate } from "../../../features/roi-request-duplicate/model/use-roi-request-duplicate";
import { RoiRequestDuplicateDialog } from "../../../features/roi-request-duplicate/ui/roi-request-duplicate-dialog";
import { RoiRequestDuplicateSuccessBanner } from "../../../features/roi-request-duplicate/ui/roi-request-duplicate-success-banner";
import { ROUTES } from "../../../system/router/routes";
import { MOCK_ROI_REQUESTS } from "./roi-requests.mock";

interface IRoiRequestsLocationState {
  editRequestId?: number;
}

const roiRequestBase = {
  patientId: "11",
  module: "roi",
  pageTitle: "All ROI Requests",

  subnav: [],

  sections: [
    {
      id: "roi-requests",
      title: "All ROI Requests",
      tables: [
        {
          id: "roi-requests-main",
          columns: [
            { key: "mrn", header: "MRN", sortable: true },
            { key: "ldaId", header: "LDA ID", sortable: true },
            { key: "createdBy", header: "Created By", sortable: true },
            { key: "dateCreated", header: "Date Created", sortable: true },
            { key: "lastUpdated", header: "Last Updated", sortable: true },
            { key: "completedBy", header: "Completed By", sortable: true },
            { key: "dateCompleted", header: "Date Completed", sortable: true },
            { key: "cancelledBy", header: "Cancelled By", sortable: true },
            { key: "dateCancelled", header: "Date Cancelled", sortable: true },
            { key: "recordsCount", header: "# Records", sortable: true },
            { key: "requestId", header: "Request ID", sortable: true },
            { key: "roiStatus", header: "Status" },
            { key: "roiActions", header: "Actions" },
          ],
          rows: mapDemoRequestsToRows(MOCK_ROI_REQUESTS),
          ui: {
            searchable: true,
            downloadable: ["csv", "xls", "pdf"],
            downloadLinks: {
              csv: "https://drive.google.com/uc?export=download&id=1f-_X9g_ByD5VZqecjZiI4w_VbFS2cALF",
              pdf: "https://drive.google.com/uc?export=download&id=1lVudmL-evzCZzXy3veIq9lF6E_XY3IJn",
              xls: "https://docs.google.com/spreadsheets/d/1W4ELK6wiHdAy6LwWQfKde09I0wTCAlk2/export?format=xlsx",
            },
            defaultRowsPerPage: 10,
          },
        },
      ],
    },
  ],
};

export const RoiRequests = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { patientID } = useParams();
  const [flowState, setFlowState] = React.useState<{
    mode: "create" | "edit";
    request?: IDemoPersistedRoiRequest | null;
  } | null>(null);
  const [cancelRequest, setCancelRequest] =
    React.useState<IDemoPersistedRoiRequest | null>(null);
  const [roiRows, setRoiRows] = React.useState(() =>
    mapDemoRequestsToRows(
      mergeDemoRoiRequests(MOCK_ROI_REQUESTS, getDemoStoredRoiRequests()),
    ),
  );
  const {
    duplicateRequest,
    duplicateSuccessRequestId,
    openDuplicateDialog,
    closeDuplicateDialog,
    handleDuplicateSuccess: setDuplicateSuccess,
    dismissDuplicateSuccess,
  } = useRoiRequestDuplicate();

  const refreshRoiRows = React.useCallback(() => {
    setRoiRows(
      mapDemoRequestsToRows(
        mergeDemoRoiRequests(MOCK_ROI_REQUESTS, getDemoStoredRoiRequests()),
      ),
    );
  }, []);

  const openEditRequest = React.useCallback((requestId: number) => {
    const request = getDemoRoiRequestById({
      requestId,
      fallbackRequests: MOCK_ROI_REQUESTS,
    });

    if (!request || request.state !== "DRAFT") {
      return false;
    }

    setFlowState({
      mode: "edit",
      request,
    });

    return true;
  }, []);

  React.useEffect(() => {
    const editRequestId = (location.state as IRoiRequestsLocationState | null)
      ?.editRequestId;

    if (typeof editRequestId !== "number") {
      return;
    }

    openEditRequest(editRequestId);
    navigate(location.pathname, { replace: true });
  }, [location.pathname, location.state, navigate, openEditRequest]);

  const handleViewRequest = React.useCallback(
    (row: Record<string, unknown>) => {
      if (!patientID) {
        return;
      }

      if (row.roiStatus !== "Completed") {
        return;
      }

      const requestId = String(row.requestId ?? "");

      if (!requestId) {
        return;
      }

      navigate(
        ROUTES.PATIENT_DETAILS_ROI_REQUEST_VIEW.replace(
          ":patientID",
          patientID,
        ).replace(":requestID", requestId),
      );
    },
    [navigate, patientID],
  );

  const handleDownloadRequest = React.useCallback(
    async (row: Record<string, unknown>) => {
      if (!patientID || row.roiStatus !== "Completed") {
        return;
      }

      const requestId = Number.parseInt(String(row.requestId ?? ""), 10);

      if (!Number.isFinite(requestId)) {
        return;
      }

      const request = getDemoRoiRequestById({
        requestId,
        fallbackRequests: MOCK_ROI_REQUESTS,
      });

      if (!request || request.state !== "COMPLETED") {
        return;
      }

      await downloadRoiRequestPdf({
        patientId: patientID,
        request,
      });
    },
    [patientID],
  );

  const getRequestByRow = React.useCallback((row: Record<string, unknown>) => {
    const requestId = Number.parseInt(String(row.requestId ?? ""), 10);

    if (!Number.isFinite(requestId)) {
      return null;
    }

    return (
      getDemoRoiRequestById({
        requestId,
        fallbackRequests: MOCK_ROI_REQUESTS,
      }) ?? null
    );
  }, []);

  const roiRequest = React.useMemo(() => {
    return {
      ...roiRequestBase,
      sections: roiRequestBase.sections.map((section) => ({
        ...section,
        tables: section.tables.map((table) => ({
          ...table,
          rows: roiRows,
          ui: {
            ...table.ui,
          },
        })),
      })),
    };
  }, [roiRows]);

  const handleCreateOpen = () => {
    dismissDuplicateSuccess();
    setFlowState({
      mode: "create",
    });
  };

  const handleCreateExit = () => {
    setFlowState(null);
    refreshRoiRows();
  };

  const handleEditRequest = React.useCallback(
    (row: Record<string, unknown>) => {
      if (row.roiStatus !== "Draft") {
        return;
      }

      const requestId = Number.parseInt(String(row.requestId ?? ""), 10);

      if (!Number.isFinite(requestId)) {
        return;
      }

      dismissDuplicateSuccess();
      openEditRequest(requestId);
    },
    [dismissDuplicateSuccess, openEditRequest],
  );

  const handleCancelRequestClick = React.useCallback(
    (row: Record<string, unknown>) => {
      if (row.roiStatus !== "Draft") {
        return;
      }

      const request = getRequestByRow(row);

      if (!request || request.state !== "DRAFT") {
        return;
      }

      setCancelRequest(request);
    },
    [getRequestByRow],
  );

  const handleDuplicateRequestClick = React.useCallback(
    (row: Record<string, unknown>) => {
      if (row.roiStatus !== "Completed") {
        return;
      }

      const request = getRequestByRow(row);

      if (!request || request.state !== "COMPLETED") {
        return;
      }

      openDuplicateDialog(request);
    },
    [getRequestByRow, openDuplicateDialog],
  );

  const handleDuplicateSuccess = React.useCallback(
    (request: IDemoPersistedRoiRequest) => {
      refreshRoiRows();
      setDuplicateSuccess(request);
    },
    [refreshRoiRows, setDuplicateSuccess],
  );

  const handleEditDuplicatedRequest = React.useCallback(() => {
    if (duplicateSuccessRequestId === null) {
      return;
    }

    const isOpened = openEditRequest(duplicateSuccessRequestId);

    if (isOpened) {
      dismissDuplicateSuccess();
    }
  }, [dismissDuplicateSuccess, duplicateSuccessRequestId, openEditRequest]);

  const handleCancelDialogClose = React.useCallback(() => {
    setCancelRequest(null);
  }, []);

  const handleConfirmCancelRequest = React.useCallback(() => {
    if (!cancelRequest || cancelRequest.state !== "DRAFT") {
      setCancelRequest(null);
      return;
    }

    saveDemoRoiRequest(
      {
        request_id: cancelRequest.request_id,
        state: "CANCELLED",
        total_selected_items_count: cancelRequest.total_selected_items_count,
        data: cancelRequest.data,
      },
      {
        baseRequest: cancelRequest,
      },
    );
    setCancelRequest(null);
    refreshRoiRows();
  }, [cancelRequest, refreshRoiRows]);

  const downloadLinks =
    roiRequest.sections[0]?.tables[0]?.ui?.downloadLinks ?? {};
  const cancelDialogCloseIcon = (
    <svg
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        d="M15.8327 4.16699L4.16602 15.8337"
        stroke="#5A5959"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M4.16602 4.16699L15.8327 15.8337"
        stroke="#5A5959"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );

  if (flowState) {
    return (
      <RoiRequestCreateFlow
        isOpen
        mode={flowState.mode}
        request={flowState.request ?? null}
        onExitToList={handleCreateExit}
      />
    );
  }

  return (
    <Styled.Container>
      {duplicateSuccessRequestId !== null ? (
        <RoiRequestDuplicateSuccessBanner
          onEdit={handleEditDuplicatedRequest}
          onDismiss={dismissDuplicateSuccess}
        />
      ) : null}
      <Styled.PageHeader>
        <Styled.Title>All ROI Requests</Styled.Title>
        <Styled.Download>
          <div>Download All</div>
          <Styled.DownloadOptions>
            <a href={downloadLinks.csv} download="roi-requests.csv">
              <svg
                width="38"
                height="38"
                viewBox="0 0 38 38"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <rect width="38" height="38" rx="6" fill="#F8F8F8" />
                <path
                  d="M11 19.9998V11.002C11 9.89651 11.8968 9.00072 13.0022 9.00195L19.9958 9.00973L27 15.9998V19.9998M20 9.49975V13.9998C20 15.1043 20.8954 15.9998 22 15.9998H26.5"
                  stroke="#181818"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M20 23H18C17.4477 23 17 23.4477 17 24V25C17 25.5523 17.4477 26 18 26H19C19.5523 26 20 26.4477 20 27V28C20 28.5523 19.5523 29 19 29H17M22.5 23L24.75 29L27 23M14.5 28C14.5 28.5523 14.0523 29 13.5 29H12C11.4477 29 11 28.5523 11 28V24C11 23.4477 11.4477 23 12 23H13.5C14.0523 23 14.5 23.4477 14.5 24"
                  stroke="#181818"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </a>
            <a href={downloadLinks.xls} download="roi-requests.xlsx">
              <svg
                width="38"
                height="38"
                viewBox="0 0 38 38"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <rect width="38" height="38" rx="6" fill="#F8F8F8" />
                <path
                  d="M17.5 23V29H21"
                  stroke="#181818"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M11 23L13 26M13 26L15 29M13 26L15 23M13 26L11 29"
                  stroke="#181818"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M27.0002 23H23.5V26H27.0002V29H23.5"
                  stroke="#181818"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M11 19.9978L11.0199 10.9956C11.0224 9.89186 11.9184 8.99877 13.0222 9L19.9958 9.00776L27 15.9948L26.9922 19.9978M20 9.49778V13.9978C20 15.1024 20.8954 15.9978 22 15.9978H26.5"
                  stroke="#181818"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </a>
            <a href={downloadLinks.pdf} download="roi-requests.pdf">
              <svg
                width="38"
                height="38"
                viewBox="0 0 38 38"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <rect width="38" height="38" rx="6" fill="#F8F8F8" />
                <path
                  d="M11 19.9978L11.0199 10.9956C11.0224 9.89186 11.9185 8.99877 13.0222 9L19.9958 9.00776L27 15.9948L26.9922 19.9978M20 9.49778V13.9978C20 15.1024 20.8954 15.9978 22 15.9978H26.5"
                  stroke="#181818"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M26.5 23.75C26.9142 23.75 27.25 23.4142 27.25 23C27.25 22.5858 26.9142 22.25 26.5 22.25V23.75ZM23 23V22.25C22.5858 22.25 22.25 22.5858 22.25 23H23ZM22.25 29C22.25 29.4142 22.5858 29.75 23 29.75C23.4142 29.75 23.75 29.4142 23.75 29H22.25ZM26 26.75C26.4142 26.75 26.75 26.4142 26.75 26C26.75 25.5858 26.4142 25.25 26 25.25V26.75ZM11 23V22.25C10.5858 22.25 10.25 22.5858 10.25 23H11ZM10.25 29C10.25 29.4142 10.5858 29.75 11 29.75C11.4142 29.75 11.75 29.4142 11.75 29H10.25ZM17 23V22.25C16.5858 22.25 16.25 22.5858 16.25 23H17ZM17 29H16.25C16.25 29.4142 16.5858 29.75 17 29.75V29ZM26.5 22.25H23V23.75H26.5V22.25ZM22.25 23V26H23.75V23H22.25ZM22.25 26V29H23.75V26H22.25ZM23 26.75H26V25.25H23V26.75ZM11 23.75H12.75V22.25H11V23.75ZM11.75 29V26.5H10.25V29H11.75ZM11.75 26.5V23H10.25V26.5H11.75ZM12.75 25.75H11V27.25H12.75V25.75ZM13.75 24.75C13.75 25.3023 13.3023 25.75 12.75 25.75V27.25C14.1307 27.25 15.25 26.1307 15.25 24.75H13.75ZM12.75 23.75C13.3023 23.75 13.75 24.1977 13.75 24.75H15.25C15.25 23.3693 14.1307 22.25 12.75 22.25V23.75ZM17 23.75H18.5V22.25H17V23.75ZM19.75 25V27H21.25V25H19.75ZM18.5 28.25H17V29.75H18.5V28.25ZM17.75 29V23H16.25V29H17.75ZM19.75 27C19.75 27.6904 19.1904 28.25 18.5 28.25V29.75C20.0188 29.75 21.25 28.5188 21.25 27H19.75ZM18.5 23.75C19.1904 23.75 19.75 24.3096 19.75 25H21.25C21.25 23.4812 20.0188 22.25 18.5 22.25V23.75Z"
                  fill="#181818"
                />
              </svg>
            </a>
          </Styled.DownloadOptions>
        </Styled.Download>
      </Styled.PageHeader>
      <SectionedDataTable
        healthEntity={roiRequest}
        onRowClick={handleViewRequest}
        onRoiViewRequest={handleViewRequest}
        onRoiDownloadRequest={handleDownloadRequest}
        onRoiDuplicateRequest={handleDuplicateRequestClick}
        onRoiCancelRequest={handleCancelRequestClick}
        onRoiEditRequest={handleEditRequest}
        headerAction={
          <RoiRequestCreateTriggerButton
            type="button"
            onClick={handleCreateOpen}
          >
            <i className="pi pi-plus" aria-hidden="true" />
            <span>Create ROI Request</span>
          </RoiRequestCreateTriggerButton>
        }
      />
      <RoiRequestDuplicateDialog
        request={duplicateRequest}
        fallbackRequests={MOCK_ROI_REQUESTS}
        visible={Boolean(duplicateRequest)}
        onClose={closeDuplicateDialog}
        onDuplicate={handleDuplicateSuccess}
      />
      <Styled.ThemedDialog
        visible={Boolean(cancelRequest)}
        modal
        dismissableMask={false}
        blockScroll
        draggable={false}
        closeIcon={cancelDialogCloseIcon}
        style={{ maxWidth: "546px" }}
        maskStyle={{
          background: "rgba(59, 68, 76, 0.6)",
        }}
        onHide={handleCancelDialogClose}
      >
        <Styled.DialogWrapper>
          <Styled.DialogTitle>Cancel ROI Request?</Styled.DialogTitle>
          <Styled.DialogText>
            Are you sure you want to cancel this ROI request? This action cannot
            be undone.
          </Styled.DialogText>
          <Styled.DialogActions>
            <Styled.SecondaryDialogButton
              type="button"
              onClick={handleCancelDialogClose}
            >
              Keep Request
            </Styled.SecondaryDialogButton>
            <Styled.DangerDialogButton
              type="button"
              onClick={handleConfirmCancelRequest}
            >
              Cancel Request
            </Styled.DangerDialogButton>
          </Styled.DialogActions>
        </Styled.DialogWrapper>
      </Styled.ThemedDialog>
    </Styled.Container>
  );
};
